/**
 * mockStates — the lifecycle states an admin can inject into the CURRENT page's
 * mock render, on demand, instead of the old hardcoded pending/deleting seeds in
 * mock/*.js (which forced every preview to show a pending cell whether you
 * wanted it or not).
 *
 * Each chip maps to the interpreter's `ctx.pending` shape
 * ({adds,updates,deleting,deleting_grids}). Unselect all → the base mock (no
 * injected states); (multi-)select → those states overlaid on the base feed.
 *
 * The four are visually DISTINCT so the admin can see each render:
 *   pending  add, no session, no image      → a bare "Starting…" skeleton
 *   adding   add, no session, WITH a thumb   → a "Starting…" skeleton with image
 *   running  add, WITH a session            → a "Logging…" skeleton (agent live)
 *   deleting an item + a session grid fading → 0.4-opacity cell / whole grid
 */
import { pageOfNode } from './focusModel';

export const MOCK_STATES = [
  { key: 'pending', label: 'Pending' },
  { key: 'adding', label: 'Adding' },
  { key: 'running', label: 'Running' },
  { key: 'deleting', label: 'Deleting' },
];

const GRID_TYPES = new Set(['ItemCollection', 'SessionCollection']);

/** Do the injectable states apply here? Only a page with a collection can show them. */
export function pageHasCollection(blueprint, pageId) {
  const comps = blueprint?.components || {};
  return Object.keys(comps).some(
    (id) => GRID_TYPES.has(comps[id]?.type) && pageOfNode(blueprint, id)?.id === pageId,
  );
}

/**
 * The region of the FIRST grid on the previewed page. An injected add is tagged
 * with it so the placeholder demos on ONE tab, not all: the runtime fail-opens
 * an UNtagged add into every scope (a real host case — a capture whose landing
 * tab the host didn't state), but as the chips' default that recreates the
 * "skeleton on every tab" the wardrobe ux_rule locks against.
 */
function firstGridRegion(blueprint, pageId) {
  const comps = blueprint?.components || {};
  const id = Object.keys(comps).find(
    (k) => GRID_TYPES.has(comps[k]?.type) && pageOfNode(blueprint, k)?.id === pageId,
  );
  return comps[id]?.region ?? null;
}

/**
 * Overlay the selected states onto a base feed. Returns a NEW feed whose
 * `pending` carries only what the chips asked for — the base's own pending is
 * replaced, so deselecting a chip removes exactly its state.
 */
export function injectStates(base, selected, { widgetId, blueprint, pageId = null }) {
  const entries = base?.entries || [];
  const firstItem = entries.find((e) => e.item_id || e.id) || null;
  const firstId = firstItem ? (firstItem.item_id || firstItem.id) : null;
  const firstSession = entries.find((e) => e.session_id || e.source_session_id) || null;
  const sid = firstSession ? (firstSession.session_id || firstSession.source_session_id) : null;
  const kind = (blueprint?.data?.kinds || [])[0] || 'item';
  const instance_id = base?.instance_id;
  // tag the add with the previewed page's grid region so it lands on one tab
  const tab = firstGridRegion(blueprint, pageId);
  const add = (client_ref, extra) => ({ client_ref, session_id: null, kind, instance_id, widget_id: widgetId, ...(tab ? { tab } : {}), ...extra });

  const pending = { adds: [], updates: [], deleting: [], deleting_grids: [] };
  if (selected.has('pending')) pending.adds.push(add('inj-pending'));
  if (selected.has('adding')) pending.adds.push(add('inj-adding', { image_url: firstItem?.image_url || null }));
  if (selected.has('running')) pending.adds.push(add('inj-running', { session_id: 'inj-session', image_url: firstItem?.image_url || null }));
  if (selected.has('deleting')) {
    if (firstId) pending.deleting.push(firstId);
    if (sid) pending.deleting_grids.push(sid);
  }
  return { ...base, pending };
}
